import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { requestStepUp } from '../services/api';

// Two steps: password + delete PIN -> code emailed to the security address -> onConfirm(requestId, code).
const StepUpDialog = ({
  open, onOpenChange, action, targetId = '', targetLabel = '', title, description,
  children, extraReady = true, extraBody = {}, confirmLabel = 'Confirm', onConfirm, testIdPrefix = 'step-up',
}) => {
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [code, setCode] = useState('');
  const [requestId, setRequestId] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setPassword(''); setPin(''); setCode(''); setRequestId(null); setBusy(false); }
  }, [open]);

  const sendCode = async () => {
    setBusy(true);
    try {
      const res = await requestStepUp({ action, targetId, targetLabel, password, pin, ...extraBody });
      setRequestId(res.data.requestId);
      toast.success('Confirmation code emailed to the security address.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send the code');
    } finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      await onConfirm(requestId, code);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="text-red-600">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {!requestId ? (
          <div className="py-2 space-y-3">
            {children}
            <Input data-testid={`${testIdPrefix}-password-input`} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your account password" />
            <Input data-testid={`${testIdPrefix}-pin-input`} type="password" inputMode="numeric" maxLength={4} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} placeholder="4-digit delete PIN" />
          </div>
        ) : (
          <div className="py-2 space-y-2">
            <p className="text-sm text-slate-600" data-testid={`${testIdPrefix}-code-sent-msg`}>Enter the 6-digit code sent to the security email. It expires in 10 minutes.</p>
            <Input data-testid={`${testIdPrefix}-code-input`} inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} placeholder="••••••" />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid={`${testIdPrefix}-cancel-btn`}>Cancel</Button>
          {!requestId ? (
            <Button variant="destructive" onClick={sendCode} disabled={!extraReady || !password || pin.length !== 4 || busy} data-testid={`${testIdPrefix}-send-code-btn`}>
              {busy ? 'Checking...' : 'Email me a code'}
            </Button>
          ) : (
            <Button variant="destructive" onClick={confirm} disabled={code.length !== 6 || busy} data-testid={`${testIdPrefix}-confirm-btn`}>
              {busy ? 'Working...' : confirmLabel}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StepUpDialog;
